/**
 * The Scrubber's arithmetic, kept out of the component so the Node runner can
 * pin it (`__tests__/scrubber-math.test.ts`); the story's play functions prove
 * the DOM wiring around it.
 *
 * Source: A2's `training/scrubber.tsx:6-100` and `profile/big-number.tsx:44-66`.
 */

/** One ruler tick per step, and one step per this many pixels of drag. */
export const STEP_PX = 22;

/** Eight ticks wide, so a tick always sits under the centre marker. */
export const RULER_PX = STEP_PX * 8;

/** PageUp / PageDown move this many steps. */
export const PAGE_STEPS = 5;

/** Decimal places a number is written with, capped so float noise cannot widen it. */
function decimalsOf(n: number): number {
  const text = String(n);
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : Math.min(6, text.length - dot - 1);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * `value` moved by `steps` whole steps, clamped. Rounded to the precision of
 * `value` and `step`, so 0.1 × 3 is 0.3 and not 0.30000000000000004 — a
 * value that would then never equal the one the caller stored.
 */
export function stepBy(
  value: number,
  steps: number,
  { step, min, max }: { step: number; min: number; max: number }
): number {
  const decimals = Math.max(decimalsOf(step), decimalsOf(value));
  const raw = value + steps * step;
  return clamp(Number(raw.toFixed(decimals)), min, max);
}

/**
 * The value under the finger `dx` pixels from where the drag began on
 * `start`. Finger RIGHT is more; a step per `STEP_PX`, rounded to the nearest
 * whole step so the value changes half-way between ticks.
 */
export function valueAtDrag(
  start: number,
  dx: number,
  bounds: { step: number; min: number; max: number },
  stepPx: number = STEP_PX
): number {
  // `+ 0` turns Math.round's -0 into 0, so a drag of a few pixels left is 0 steps.
  return stepBy(start, Math.round(dx / stepPx) + 0, bounds);
}

/**
 * The ruler's `background-position-x`: with the finger while dragging (`dx`
 * set), with the value otherwise. Either way one tick per step, so the drag,
 * the keys and −/+ read as the same dial.
 */
export function rulerOffset(
  value: number,
  step: number,
  drag: { start: number; dx: number } | null,
  stepPx: number = STEP_PX
): number {
  if (drag === null) {
    return (value / step) * stepPx;
  }
  return (drag.start / step) * stepPx + drag.dx;
}

export type KeyMove =
  | { kind: "steps"; steps: number }
  | { kind: "to"; edge: "min" | "max" };

/**
 * What a key does to the value, or `null` for a key the scrubber leaves
 * alone (Tab, Escape…), which must keep propagating.
 */
export function keyMove(key: string): KeyMove | null {
  switch (key) {
    case "ArrowRight":
    case "ArrowUp":
      return { kind: "steps", steps: 1 };
    case "ArrowLeft":
    case "ArrowDown":
      return { kind: "steps", steps: -1 };
    case "PageUp":
      return { kind: "steps", steps: PAGE_STEPS };
    case "PageDown":
      return { kind: "steps", steps: -PAGE_STEPS };
    case "Home":
      return { kind: "to", edge: "min" };
    case "End":
      return { kind: "to", edge: "max" };
    default:
      return null;
  }
}

/** The value after `move`, clamped to the bounds. */
export function applyKeyMove(
  value: number,
  move: KeyMove,
  bounds: { step: number; min: number; max: number }
): number {
  if (move.kind === "to") {
    return move.edge === "min" ? bounds.min : bounds.max;
  }
  return stepBy(value, move.steps, bounds);
}
